import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Truck, Basket, BadgeCheck } from "lucide-react";
import { IMAGES } from "@/lib/catalog";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "23+", label: "Years of trust" },
  { value: "500+", label: "Daily essentials" },
  { value: "2,000+", label: "Families served" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-bone" data-testid="hero-section">
      <div className="grain pointer-events-none absolute inset-0 opacity-[0.05]" />
      <div className="pointer-events-none absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-saffron/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -left-32 w-[24rem] h-[24rem] rounded-full bg-forest/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.45 }}
            className="text-xs font-semibold uppercase tracking-[0.3em] text-marigold"
          >
            Est. 2002 · Main Market Road, Kanpur
          </motion.p>

          <h1 className="mt-6 font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-ink">
            <Line delay={1.5}>Your neighbourhood</Line>
            <Line delay={1.62}>
              <span className="italic text-forest">kirana</span>, stocked
            </Line>
            <Line delay={1.74}>
              with <span className="italic text-marigold">care.</span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 2.0 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-ink-soft leading-relaxed"
          >
            Fresh atta, pure desi ghee, daily milk and everything in between —
            hand-checked every morning and delivered to your doorstep the same day.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 2.15 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/products"
              data-testid="hero-explore-button"
              className="group inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-bone hover:bg-forest-deep transition-colors duration-300"
            >
              Explore the Store
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-forest/20 px-7 py-3.5 text-sm font-semibold text-forest hover:border-forest hover:bg-forest hover:text-bone transition-colors duration-300"
            >
              Send an Enquiry
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 2.35 }}
            className="mt-12 flex items-center gap-8 sm:gap-12"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-serif font-bold text-2xl sm:text-3xl text-forest">{s.value}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.15em] text-ink-muted">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div style={{ y: yImg }} className="relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.2, delay: 1.7, ease: [0.76, 0, 0.24, 1] }}
              className="overflow-hidden rounded-t-[10rem] rounded-b-3xl shadow-2xl shadow-forest/20"
            >
              <motion.img
                src={IMAGES.hero}
                alt="Fresh vegetables and groceries at Shivam Kirana Store"
                className="h-[400px] lg:h-[520px] w-full object-cover"
                initial={{ scale: 1.25 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, delay: 1.7, ease: [0.22, 1, 0.36, 1] }}
                data-testid="hero-image"
              />
            </motion.div>
          </motion.div>

          <motion.div
            style={{ y: yBadge }}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 2.5 }}
            className="absolute -bottom-6 -left-4 sm:-left-8"
          >
            <div className="animate-float-slow flex items-center gap-3 rounded-2xl bg-white/90 backdrop-blur-md border border-line px-5 py-4 shadow-xl shadow-forest/10" data-testid="hero-delivery-badge">
              <span className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center">
                <Truck className="w-5 h-5 text-forest" />
              </span>
              <div>
                <p className="text-sm font-bold text-ink">Free doorstep delivery</p>
                <p className="text-xs text-ink-muted">Same day, within the neighbourhood</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 2.7 }}
            className="absolute -top-5 -right-2 sm:-right-5 flex items-center gap-2 rounded-full bg-marigold text-white px-4 py-2 shadow-lg"
          >
            <BadgeCheck className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-wider">Quality checked daily</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
